import FingerprintJS from '@fingerprintjs/fingerprintjs';
import type { EventBus } from '../EventBus';
import type { LocalStorageService } from './browser/LocalStorageService';
import type { TrackingDebugService } from './TrackingDebugService';
import { StorageKey } from '../config/storageKeys';
import { TrackingBusEvent } from '../constants/trackingBusEvent';
import { TrackingTag } from '../constants/trackingTag';

/**
 * Stable **device** visitor id via FingerprintJS, persisted in localStorage (cross-session). The **`inflight`**
 * promise collapses concurrent `getDeviceId()` calls while the library loads. **`getCachedDeviceId()`** is
 * synchronous for bfcache / `pagehide` paths where awaiting the fingerprint would miss the page-freeze window.
 */
export class DeviceService {
  private inMemoryDeviceId: string | null = null;
  private inflight: Promise<string> | null = null;

  constructor(
    private readonly storage: LocalStorageService,
    private readonly bus: EventBus,
    private readonly debug?: TrackingDebugService,
  ) {}

  init(): void {
    const cached = this.storage.get(StorageKey.DeviceId);
    if (cached) {
      this.inMemoryDeviceId = cached;
    }

    this.bus.subscribe(
      TrackingBusEvent.ConsentWithdrawn,
      () => {
        this.storage.remove(StorageKey.DeviceId);
        this.inMemoryDeviceId = null;
        this.inflight = null;
      },
      TrackingTag.DeviceService,
    );
  }

  /** Sync read for bfcache unload: fingerprint await must not run before the page freezes. */
  getCachedDeviceId(): string | null {
    if (this.inMemoryDeviceId) {
      return this.inMemoryDeviceId;
    }

    return this.storage.get(StorageKey.DeviceId);
  }

  async getDeviceId(): Promise<string> {
    if (this.inMemoryDeviceId) {
      this.debug?.log('DeviceService', 'getDeviceId', { source: 'memory' });

      return this.inMemoryDeviceId;
    }

    const cached = this.storage.get(StorageKey.DeviceId);
    if (cached) {
      this.inMemoryDeviceId = cached;
      this.debug?.log('DeviceService', 'getDeviceId', { source: 'localStorage' });

      return cached;
    }

    if (this.inflight) {
      return await this.inflight;
    }

    this.inflight = (async () => {
      const t0 = typeof performance !== 'undefined' ? performance.now() : 0;
      this.debug?.log('DeviceService', 'FingerprintJS load start', {});
      const fp = await FingerprintJS.load();
      const result = await fp.get();
      const id = result.visitorId;
      const dt = typeof performance !== 'undefined' ? Math.round(performance.now() - t0) : null;
      this.debug?.log('DeviceService', 'FingerprintJS done', { ms: dt });
      this.storage.set(StorageKey.DeviceId, id);
      this.inMemoryDeviceId = id;
      this.inflight = null;

      return id;
    })();

    return await this.inflight;
  }
}
